import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Building2, ExternalLink, Linkedin, Globe, Package, Plus, X, Mail, User, DollarSign, Percent, Calendar, UserCircle, Layers, Activity, Repeat, Tag } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmModal";
import PragmaSelect from "../components/PragmaSelect";
import { useSettings, formatMoney } from "../lib/settings";
import StatusBadge from "../components/StatusBadge";
import MovesTimeline from "../components/MovesTimeline";
import NotesTimeline from "../components/NotesTimeline";
import HistoryTimeline from "../components/HistoryTimeline";
import DealProposalsCard from "../components/DealProposalsCard";
import LostReasonModal from "../components/LostReasonModal";
import WonConfirmModal from "../components/WonConfirmModal";
import WonExplosion from "../components/WonExplosion";
import DetailsCard from "../components/DetailsCard";
import DealAIInsights from "../components/DealAIInsights";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const { currency } = useSettings();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [deal, setDeal] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyFields, setCompanyFields] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const load = async () => {
    try {
      const [d, p, c, u, dv] = await Promise.all([
        api.get(`/deals/${id}`), api.get("/pipelines"),
        api.get("/contacts"), api.get("/users"),
        api.get("/deliverables"),
      ]);
      setDeal(d.data); setPipelines(p.data); setContacts(c.data); setUsers(u.data);
      setCatalog(dv.data || []);
      if (d.data.company_id) {
        try {
          const [co, cf] = await Promise.all([
            api.get(`/companies/${d.data.company_id}`),
            api.get(`/custom-fields?entity=company`),
          ]);
          setCompany(co.data);
          setCompanyFields(cf.data.filter(f => f.active));
        } catch { setCompany(null); }
      } else { setCompany(null); }
    } catch { toast.error("Not found"); navigate("/deals"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    try {
      const { data: updated } = await api.patch(`/deals/${id}`, data);
      setDeal(updated); setHistoryKey(k => k + 1); toast.success("Saved");
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("lost reason")) {
        // Caller will surface picker. Don't toast — silent so the modal can handle.
        return "needs_lost_reason";
      }
      toast.error("Failed");
      return false;
    }
  };

  const [showLostModal, setShowLostModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showExplosion, setShowExplosion] = useState(false);
  const [pendingStageId, setPendingStageId] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "lost" && deal.status !== "lost") {
      setPendingStageId(null);
      setShowLostModal(true);
      return;
    }
    if (newStatus === "won" && deal.status !== "won") {
      setPendingStageId(null);
      setShowWonModal(true);
      return;
    }
    await patch({ status: newStatus });
  };

  const handleStageChange = async (newStageId) => {
    const stage = stages.find((s) => s.id === newStageId);
    if (!stage) return;
    const stageName = stage.name.toLowerCase();
    if (stageName.includes("lost") && deal.status !== "lost") {
      setPendingStageId(newStageId);
      setShowLostModal(true);
      return;
    }
    if (stageName.includes("won") && deal.status !== "won") {
      setPendingStageId(newStageId);
      setShowWonModal(true);
      return;
    }
    await patch({ stage_id: newStageId });
  };

  const confirmLostWithReason = async ({ reasonId, notes }) => {
    const body = {
      status: "lost",
      lost_reason_id: reasonId,
      lost_reason_notes: notes || "",
    };
    if (pendingStageId) body.stage_id = pendingStageId;
    const ok = await patch(body);
    if (ok === true) {
      setShowLostModal(false);
      setPendingStageId(null);
    }
  };

  const confirmWonWithEngagement = async ({ engagementType, engagementEndDate }) => {
    const body = {
      engagement_type: engagementType,
      engagement_end_date: engagementEndDate,
    };
    if (pendingStageId) body.stage_id = pendingStageId;
    else body.status = "won";
    const ok = await patch(body);
    if (ok === true) {
      setShowWonModal(false);
      setPendingStageId(null);
      setShowExplosion(true);
    }
  };

  const remove = async () => {
    if (!await confirm({ title: "Delete this deal?", message: "All moves, notes, deliverables and history for this deal will be removed. This cannot be undone.", destructive: true, confirmLabel: "Delete deal" })) return;
    await api.delete(`/deals/${id}`);
    toast.success("Deleted");
    navigate("/deals");
  };

  if (!deal) return <div className="px-8 py-12 text-mute mono text-xs">loading…</div>;
  const pipeline = pipelines.find(p => p.id === deal.pipeline_id);
  const stages = pipeline ? [...pipeline.stages].sort((a, b) => a.order - b.order) : [];
  const currentStage = stages.find(s => s.id === deal.stage_id);
  const contact = contacts.find(c => c.id === deal.contact_id);

  const statusColor = deal.status === "won" ? "teal" : deal.status === "lost" ? "red" : "purple";

  return (
    <div data-testid="deal-detail-page">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/deals")} className="pg-btn pg-btn-ghost"><ArrowLeft size={14} /> Back</button>
          <div>
            <h1 className="text-2xl" style={{ fontWeight: 600 }}>{deal.name}</h1>
            <div className="mono text-[11px] text-mute mt-1">
              {pipeline?.name} · {currentStage?.name} · {formatMoney(deal.value, currency.symbol)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PragmaSelect
            value={deal.stage_id}
            onChange={handleStageChange}
            options={stages.map((s) => ({ value: s.id, label: s.name, hint: s.probability != null ? `${s.probability}% win probability` : undefined }))}
            testId="deal-header-stage"
            size="sm"
            style={{ minWidth: 180 }}
          />
          <StatusBadge color={statusColor} label={deal.status} />
          <button onClick={remove} className="pg-btn pg-btn-danger" data-testid="delete-deal-btn" style={{ display: canDelete ? undefined : "none" }}><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-2 space-y-6">
          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Contact</h3>
            {contact ? (
              <Link to={`/contacts/${contact.id}`} className="block">
                <div className="text-sm" style={{ color: "var(--pg-text)" }}>{contact.name}</div>
                <div className="mono text-[11px] text-mute mt-1">{contact.company || "—"}</div>
                <div className="mono text-[11px] text-mute">{contact.email || "—"}</div>
              </Link>
            ) : <div className="text-mute mono text-xs">No contact linked</div>}
          </div>

          <div className="pg-card p-6" data-testid="deal-company-card">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Company</h3>
            {company ? (
              <div className="space-y-2 text-sm">
                <Link to={`/companies/${company.id}`} className="inline-flex items-center gap-2"
                      style={{ color: "var(--pg-text)", fontWeight: 600 }}>
                  <Building2 size={14} /> {company.name}
                </Link>
                {company.domain && (
                  <div>
                    <a href={`https://${company.domain}`} target="_blank" rel="noreferrer"
                       className="mono text-[11px] inline-flex items-center gap-1"
                       style={{ color: "var(--pg-accent-deep)" }}>
                      {company.domain} <ExternalLink size={10} />
                    </a>
                  </div>
                )}
                {company.industry && (
                  <div className="text-mute"><span className="mono text-[11px] text-mute">Industry</span> · {company.industry}</div>
                )}
                {company.employees && (
                  <div className="text-mute"><span className="mono text-[11px] text-mute">Employees</span> · {company.employees}</div>
                )}
                <div className="flex items-center gap-3 pt-1">
                  {company.website && (
                    <a href={company.website} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--pg-accent-deep)" }}>
                      <Globe size={12} /> Website
                    </a>
                  )}
                  {company.linkedin_url && (
                    <a href={company.linkedin_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--pg-accent-deep)" }}>
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                </div>
                {companyFields.length > 0 && (
                  <div className="pt-2 mt-2 space-y-1" style={{ borderTop: "1px dashed var(--pg-border)" }}>
                    {companyFields.map(f => {
                      const v = (company.custom_fields || {})[f.key];
                      if (v === undefined || v === "" || v === null) return null;
                      return (
                        <div key={f.id} className="text-mute">
                          <span className="mono text-[11px] text-mute">{f.label}</span> · <span style={{ color: "var(--pg-text)" }}>{String(v)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mono text-[11px] text-mute mt-2">Edits are made from the Company record.</div>
              </div>
            ) : (
              <div className="text-mute mono text-xs">No company linked</div>
            )}
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Forecast</h3>
            <div className="text-2xl mono" style={{ color: "var(--pg-accent-deep)" }}>
              {formatMoney((deal.value || 0) * (deal.probability || 0) / 100, currency.symbol)}
            </div>
            <div className="mono text-[11px] text-mute mt-1">weighted by {deal.probability}%</div>
          </div>

          {deal.status === "lost" && deal.lost_reason_title && (
            <div
              className="pg-card p-6"
              data-testid="deal-lost-reason-card"
              style={{ borderColor: "rgba(239, 68, 68, 0.30)" }}
            >
              <h3 className="text-sm mono uppercase tracking-wider mb-3" style={{ color: "var(--pg-red)" }}>
                Lost Reason
              </h3>
              <div className="text-sm" style={{ color: "var(--pg-text)" }}>{deal.lost_reason_title}</div>
              {deal.lost_reason_notes && (
                <div className="mono text-[11px] text-mute mt-2" style={{ lineHeight: 1.55 }}>
                  {deal.lost_reason_notes}
                </div>
              )}
              <button
                onClick={() => setShowLostModal(true)}
                className="mono text-[11px] underline mt-3"
                style={{ color: "var(--pg-text-dim)" }}
                data-testid="edit-lost-reason"
              >
                edit reason
              </button>
            </div>
          )}

          <DealDeliverablesCard
            deal={deal}
            catalog={catalog}
            currency={currency}
            onUpdate={(items) => patch({ deliverables: items })}
          />

          <DealProposalsCard dealId={deal.id} />

          <NotesTimeline linkedType="deal" linkedId={deal.id} />
          <MovesTimeline linkedType="deal" linkedId={deal.id} users={users} contactEmail={contact?.email} contactPhone={contact?.phone} />
          <HistoryTimeline key={historyKey} entityType="deal" entityId={deal.id} />

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Stage History</h3>
            <div className="space-y-2">
              {(deal.stage_history || []).map((h, i) => {
                const st = stages.find(s => s.id === h.stage_id);
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{st?.name || h.stage_id}</span>
                    <span className="mono text-[11px] text-mute">{(h.changed_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <DealAIInsights dealId={deal.id} historyKey={historyKey} />

          <DetailsCard
            testId="deal-details-card"
            createdAt={deal.created_at}
            updatedAt={deal.updated_at}
            onSave={async (data) => {
              // Intercept complex changes that need modals / side effects
              if ("stage_id" in data) return handleStageChange(data.stage_id);
              if ("status" in data) return handleStatusChange(data.status);
              if ("pipeline_id" in data) {
                const np = pipelines.find(p => p.id === data.pipeline_id);
                const firstStage = np?.stages.slice().sort((a, b) => a.order - b.order)[0];
                return await patch({ pipeline_id: data.pipeline_id, stage_id: firstStage?.id });
              }
              return await patch(data);
            }}
            fields={[
              { key: "name",  label: "Name",  value: deal.name,  icon: User },
              { key: "value", label: `Value (${currency.code})`, value: deal.value, icon: DollarSign, type: "number",
                editable: false,
                display: (v) => v != null ? formatMoney(v, currency.symbol) : "—" },
              { key: "stage_id", label: "Stage", value: deal.stage_id, icon: Layers, type: "select",
                options: stages.map(s => ({ value: s.id, label: `${s.name} (${s.probability}%)` })),
                display: () => (stages.find(s => s.id === deal.stage_id)?.name || "—") },
              { key: "probability", label: "Probability", value: deal.probability, icon: Percent, type: "number",
                display: (v) => v != null ? `${v}%` : "—" },
              { key: "expected_close_date", label: "Expected close", value: (deal.expected_close_date || "").slice(0, 10),
                icon: Calendar, type: "date",
                display: (v) => v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—" },
              { key: "owner_id", label: "Owner", value: deal.owner_id, icon: UserCircle, type: "select",
                options: users.map(u => ({ value: u.id, label: u.name })),
                display: () => (users.find(u => u.id === deal.owner_id)?.name || "—") },
              { key: "pipeline_id", label: "Pipeline", value: deal.pipeline_id, icon: Layers, type: "select",
                options: pipelines.map(p => ({ value: p.id, label: p.name })),
                display: () => (pipelines.find(p => p.id === deal.pipeline_id)?.name || "—") },
              { key: "status", label: "Status", value: deal.status, icon: Activity, type: "select",
                options: [
                  { value: "open", label: "Open" },
                  { value: "won", label: "Won" },
                  { value: "lost", label: "Lost" },
                ] },
              { key: "engagement_type", label: "Engagement", value: deal.engagement_type, icon: Repeat, type: "select",
                options: [
                  { value: "", label: "— choose —" },
                  { value: "one_off", label: "One-Off" },
                  { value: "recurring", label: "Recurring" },
                ],
                display: () => deal.engagement_type ? (deal.engagement_type === "one_off" ? "One-Off" : "Recurring") : "—" },
              { key: "engagement_end_date", label: "Engagement end", value: (deal.engagement_end_date || "").slice(0, 10),
                icon: Calendar, type: "date",
                display: (v) => v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—" },
              { key: "source", label: "Source", value: deal.source, icon: Tag },
            ]}
          />
        </div>
      </div>

      {showLostModal && (
        <LostReasonModal
          dealName={deal.name}
          existingReasonId={deal.lost_reason_id}
          existingNotes={deal.lost_reason_notes}
          onCancel={() => {
            setShowLostModal(false);
            setPendingStageId(null);
          }}
          onConfirm={confirmLostWithReason}
        />
      )}

      {showWonModal && (
        <WonConfirmModal
          dealName={deal.name}
          defaultEngagementType={deal.engagement_type}
          defaultEngagementEndDate={deal.engagement_end_date}
          onCancel={() => {
            setShowWonModal(false);
            setPendingStageId(null);
          }}
          onConfirm={confirmWonWithEngagement}
        />
      )}

      {showExplosion && (
        <WonExplosion onDone={() => setShowExplosion(false)} />
      )}
    </div>
  );
}

function DealDeliverablesCard({ deal, catalog, currency, onUpdate }) {
  const items = deal.deliverables || [];
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");

  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onUpdate(next);
  };

  const add = () => {
    if (!selected) return;
    const cat = catalog.find((c) => c.id === selected);
    if (!cat) return;
    const next = [
      ...items,
      {
        deliverable_id: cat.id,
        name: cat.name,
        description: cat.description,
        category: cat.category,
        quantity: parseFloat(qty) || 1,
        price: price === "" ? Number(cat.default_price || 0) : parseFloat(price) || 0,
      },
    ];
    onUpdate(next);
    setAdding(false); setSelected(""); setQty(1); setPrice("");
  };

  const total = items.reduce((acc, it) => acc + (Number(it.price || 0) * Number(it.quantity || 1)), 0);

  return (
    <div className="pg-card p-6" data-testid="deal-deliverables-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm mono uppercase text-mute tracking-wider">
          <Package size={14} className="inline mr-2 -mt-0.5" />
          Deliverables
        </h3>
        {!adding && (
          <button onClick={() => setAdding(true)} className="pg-btn" data-testid="add-deliverable-btn">
            <Plus size={14} /> Add
          </button>
        )}
      </div>
      {items.length === 0 && !adding && (
        <div className="text-mute mono text-xs">No deliverables attached yet.</div>
      )}
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex items-center gap-3 p-3" style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
            <div className="flex-1">
              <div className="text-sm" style={{ color: "var(--pg-text)" }}>{it.name}</div>
              <div className="mono text-[11px] text-mute mt-0.5">
                {it.category || "general"} · qty {it.quantity || 1} × {formatMoney(it.price, currency.symbol)} = {formatMoney(Number(it.price || 0) * Number(it.quantity || 1), currency.symbol)}
              </div>
            </div>
            <button onClick={() => remove(idx)} className="text-mute hover:text-[var(--pg-red)]" data-testid={`remove-deliverable-${idx}`}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      {items.length > 0 && (
        <div className="mono text-[11px] text-mute mt-3 text-right" data-testid="deal-deliverables-total">
          Deal Value (auto): <span style={{ color: "var(--pg-text)" }}>{formatMoney(total, currency.symbol)}</span>
        </div>
      )}
      {adding && (
        <div className="mt-4 p-3" style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-6">
              <label className="block mono text-[10px] text-mute mb-1 uppercase tracking-wider">Deliverable</label>
              <PragmaSelect
                value={selected}
                onChange={(v) => {
                  setSelected(v);
                  const c = catalog.find((c) => c.id === v);
                  if (c && price === "") setPrice(String(c.default_price));
                }}
                options={[{ value: "", label: "Choose…" }, ...catalog.map((c) => ({ value: c.id, label: c.name, hint: c.category || undefined }))]}
                placeholder="Choose…"
                testId="select-deliverable"
              />
            </div>
            <div className="col-span-2">
              <label className="block mono text-[10px] text-mute mb-1 uppercase tracking-wider">Qty</label>
              <input className="pg-input" type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <div className="col-span-3">
              <label className="block mono text-[10px] text-mute mb-1 uppercase tracking-wider">Price ({currency.code})</label>
              <input className="pg-input" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="default" />
            </div>
            <div className="col-span-1 flex items-end">
              <button onClick={add} className="pg-btn pg-btn-primary" style={{ width: "100%" }} data-testid="confirm-add-deliverable">Add</button>
            </div>
          </div>
          {catalog.length === 0 && (
            <div className="mono text-[10px] text-mute mt-2">No deliverables in your catalog. Create some in the Deliverables page first.</div>
          )}
          <button onClick={() => { setAdding(false); setSelected(""); setQty(1); setPrice(""); }} className="mono text-[11px] text-mute mt-2 underline">cancel</button>
        </div>
      )}
    </div>
  );
}

